import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { Primitive } from '@coverflex-tech/hypernova-utils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { jwtDecode } from 'jwt-decode';

interface Token {
  sub: string;
}

export const TOKEN = 'cvrflx_flightdeck_token';
export const REFRESH_TOKEN = 'cvrflx_flightdeck_refresh_token';
export const USER_AGENT_TOKEN = 'cvrflx_flightdeck_user_agent_token';
export const LANGUAGE = 'cvrflex_flightdeck_language';
export const FIRST_TIME_BENEFIT = 'cvrflex_flightdeck_benefit';
export const FIRST_TIME_WITHDRAW_CREDITS =
  'cvrflex_flightdeck_withdraw_credits';
export const FRINGE_LIMIT = 'cvrflex_flightdeck_fringe_limit';
export const INSURANCE_UPGRADE_PLAN_BANNER_DISMISSED =
  'cvrflx_flightdeck_insurance_upgrade_plan_banner_dismissed';
export const ALERT_OFFBOARDING_MEAL_BALANCE_DISMISSED =
  'cvrflx_flightdeck_offboarding_meal_balance_alert_dismissed';

export const STORAGE_KEY = 'cvrflx_flightdeck_storage';
export const NOTIFICATION_TOKEN = 'cvrflx_notification_token';
export const STORAGE_OPTIONS = {
  CREDITS_AUTO_RECHARGE_LEARNING_CENTER_VISITED:
    'credits_auto_recharge_learning_center_visited',
  CREDITS_WITHDRAW_LEARNING_CENTER_VISITED:
    'credits_withdraw_learning_center_visited',
  EXPIRING_CARD_FLOW_DISMISSED:
    'cvrflex_flightdeck_expiring_card_flow_dismissed',
  CHECK_DIGITAL_CARD_DETAILS_DISMISSED:
    'cvrflex_flightdeck_check_digital_card_details_dismissed',
  SINGLE_MEAL_PAYMENT_MODAL_LAST_VIEW:
    'cvrflex_employee_single_meal_payment_modal_last_view',
  ACTIVE_BUDGETS_COUNT: 'active_budgets_count',
} as const;

export const LOGGED_IN_USER_SHORT_NAME = 'cvrflex_logged_in_user_short_name';

export const memoryStorage = (() => {
  const storage: Record<string, string> = {};
  return {
    setItem: (key: string, data: string) => {
      return new Promise((resolve) => {
        storage[key] = data;
        resolve(data);
      });
    },
    getItem: (key: string): Promise<string | undefined> =>
      new Promise((resolve) => {
        resolve(storage[key]);
      }),
    removeItem: (key: string) => {
      return new Promise((resolve) => {
        delete storage[key];
        resolve(undefined);
      });
    },
  };
})();

const isStorageAvailable = () => {
  // Hard test for storage availability
  if (Platform.OS === 'web') {
    try {
      const test = 'cvrflx_flightdeck_available';
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
  return true;
};

const getStorage = () => {
  if (isStorageAvailable()) {
    return AsyncStorage;
  }

  return memoryStorage;
};

const getAuthToken = () => getStorage().getItem(TOKEN);
const setAuthToken = (token: string) => getStorage().setItem(TOKEN, token);
const getLanguage = () => getStorage().getItem(LANGUAGE);
const setLanguage = (language: string) =>
  getStorage().setItem(LANGUAGE, language);
const clearAuthToken = () => getStorage().removeItem(TOKEN);
const decodeAuthToken = (token: string) => {
  try {
    const decoded: Token = jwtDecode(token);
    const tokenData = JSON.parse(decoded.sub);
    return tokenData;
  } catch {
    return null;
  }
};
const getRefreshToken = () => getStorage().getItem(REFRESH_TOKEN);
const setRefreshToken = (token: string) =>
  getStorage().setItem(REFRESH_TOKEN, token);

export const getUserAgentToken = () => getStorage().getItem(USER_AGENT_TOKEN);

export const setUserAgentToken = (token: string) =>
  getStorage().setItem(USER_AGENT_TOKEN, token);

export const clearUserAgentToken = () =>
  getStorage().removeItem(USER_AGENT_TOKEN);
const clearRefreshToken = () => getStorage().removeItem(REFRESH_TOKEN);
const updateTokens = async (
  authToken: string,
  refreshToken: string,
  userAgentToken?: string
) => {
  await setAuthToken(authToken);
  await setRefreshToken(refreshToken);
  if (userAgentToken !== undefined) {
    await setUserAgentToken(userAgentToken);
  }
};

const getNotificationToken = () => getStorage().getItem(NOTIFICATION_TOKEN);
const setNotificationToken = (token: string) =>
  getStorage().setItem(NOTIFICATION_TOKEN, token);
const clearNotificationToken = () =>
  getStorage().removeItem(NOTIFICATION_TOKEN);

const getLoggedInUserShortName = () =>
  getStorage().getItem(LOGGED_IN_USER_SHORT_NAME);
const setLoggedInUserShortName = (shortName: string) =>
  getStorage().setItem(LOGGED_IN_USER_SHORT_NAME, shortName);
const clearLoggedInUserShortName = () =>
  getStorage().removeItem(LOGGED_IN_USER_SHORT_NAME);

const clearTokens = async () => {
  await clearAuthToken();
  await clearRefreshToken();
  await clearNotificationToken();
};

const getFirstTimeBenefit = () => getStorage().getItem(FIRST_TIME_BENEFIT);
const setFirstTimeBenefit = () =>
  getStorage().setItem(FIRST_TIME_BENEFIT, 'false');

export const getFringeLimit = () => getStorage().getItem(FRINGE_LIMIT);
export const setFringeLimit = () => getStorage().setItem(FRINGE_LIMIT, 'true');

export const setInsuranceUpgradePlanBannerDismissed = (dismissed: boolean) => {
  if (dismissed) {
    getStorage().setItem(INSURANCE_UPGRADE_PLAN_BANNER_DISMISSED, 'true');
  } else {
    getStorage().removeItem(INSURANCE_UPGRADE_PLAN_BANNER_DISMISSED);
  }
};
export const getInsuranceUpgradePlanBannerDismissed = async () => {
  return (
    (await getStorage().getItem(INSURANCE_UPGRADE_PLAN_BANNER_DISMISSED)) ===
    'true'
  );
};

export const setAlertOffboardingMealBalanceDismissed = async (
  dismissed: boolean
) => {
  if (dismissed) {
    await getStorage().setItem(
      ALERT_OFFBOARDING_MEAL_BALANCE_DISMISSED,
      'true'
    );
  } else {
    await getStorage().removeItem(ALERT_OFFBOARDING_MEAL_BALANCE_DISMISSED);
  }
};
export const getAlertOffboardingMealBalanceDismissed = async () => {
  return (
    (await getStorage().getItem(ALERT_OFFBOARDING_MEAL_BALANCE_DISMISSED)) ===
    'true'
  );
};

export type TStorageOption =
  (typeof STORAGE_OPTIONS)[keyof typeof STORAGE_OPTIONS];

export const setStorageItem = async <T extends Primitive = string>(
  key: TStorageOption,
  value: T
): Promise<T> => {
  const storage = (await getStorage().getItem(STORAGE_KEY)) ?? '{}';
  let storageData: {
    [key in TStorageOption]?: T;
  } = {};
  try {
    storageData = JSON.parse(storage);
  } catch {}

  storageData[key] = value;
  await getStorage().setItem(STORAGE_KEY, JSON.stringify(storageData));
  return value;
};

export const getStorageItem = async <T extends Primitive = string>(
  key: TStorageOption
): Promise<T | undefined> => {
  const storage = (await getStorage().getItem(STORAGE_KEY)) ?? '{}';

  try {
    return JSON.parse(storage)?.[key];
  } catch {
    return undefined;
  }
};

export const clearStorage = async () => {
  await getStorage().removeItem(STORAGE_KEY);
};

export const useStorageState = <T extends Primitive = string>(
  key: TStorageOption
) => {
  const [value, setValue] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadValue() {
      const value = await getStorageItem<T>(key);
      setValue(value);
      setIsLoading(false);
    }
    loadValue();
  }, [key]);

  const setStoredValue = useCallback(
    async (newValue: T) => {
      await setStorageItem(key, newValue);
      setValue(newValue);
      return newValue;
    },
    [key]
  );

  return { value, isLoading, setStoredValue };
};

export const StorageService = {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  decodeAuthToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
  getUserAgentToken,
  setUserAgentToken,
  clearUserAgentToken,
  updateTokens,
  clearTokens,
  getLanguage,
  setLanguage,
  getFirstTimeBenefit,
  setFirstTimeBenefit,
  getFringeLimit,
  setFringeLimit,
  getInsuranceUpgradePlanBannerDismissed,
  setInsuranceUpgradePlanBannerDismissed,
  setNotificationToken,
  getNotificationToken,
  clearNotificationToken,
  getLoggedInUserShortName,
  setLoggedInUserShortName,
  clearLoggedInUserShortName,
  getStorageItem,
  setStorageItem,
  clearStorage,
};
